import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
  alpha,
  Paper,
  Divider,
  LinearProgress,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import { Project, Section, Subsection, Item, ItemStatus, AISuggestion } from '../models/types';
import { exportProject } from '../utils/exportUtils';
import { aiService } from '../services/aiService';
import { useDebouncedCallback, useDetectSlowRender } from '../hooks/usePerformance';
import { withPerformanceOptimization } from '../utils/withPerformanceOptimization';
import ProjectEditor from './ProjectEditor';

interface UnifiedProjectViewProps {
  project: Project;
  onSave: (project: Project) => void;
  useCubaDashboard?: boolean;
}

// Dashboard Metric Card component
const MetricCard = memo(({
  title,
  value,
  total,
  icon,
  color,
  percentage,
}: {
  title: string;
  value: number | string;
  total?: number;
  icon: React.ReactNode;
  color: string;
  percentage?: number;
}) => {
  const theme = useTheme();
  
  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        borderRadius: 2,
        boxShadow: theme.shadows[2],
        transition: 'transform 0.3s',
        overflow: 'visible',
        border: `1px solid ${alpha(color, 0.1)}`,
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[4],
          borderColor: alpha(color, 0.3),
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 48,
              height: 48,
              mr: 2
            }}
          >
            {icon}
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" component="div" fontWeight="bold">
              {value}
              {total !== undefined && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  of {total}
                </Typography>
              )}
            </Typography>
          </Box>
        </Box>
        {percentage !== undefined && (
          <>
            <LinearProgress 
              variant="determinate" 
              value={percentage} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: alpha(color, 0.1),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: color,
                }
              }} 
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
              {Math.round(percentage)}%
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
});

// Section Progress Card
const SectionProgressCard = memo(({ section }: { section: Section }) => {
  const theme = useTheme();
  const color = getProgressColor(section.completionPercentage);
  
  return (
    <Card 
      sx={{ 
        p: 2, 
        height: '100%',
        borderRadius: 2,
        transition: 'transform 0.2s ease',
        border: `1px solid ${alpha(color, 0.1)}`,
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[3],
          borderColor: alpha(color, 0.3),
        }
      }}
    >
      <CardContent sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.1),
                color: color,
                width: 40,
                height: 40,
                mr: 2
              }}
            >
              {section.isCore ? <StarIcon /> : <DescriptionIcon />}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" component="div" fontWeight="medium" noWrap>
                {section.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Weight: {section.weight}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${Math.round(section.completionPercentage)}%`}
            size="small"
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              fontWeight: 'bold',
              border: `1px solid ${alpha(color, 0.3)}`
            }}
          />
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={section.completionPercentage} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: alpha(color, 0.1),
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
            }
          }} 
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {getProgressLabel(section.completionPercentage)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Subsections: {section.subsections.length}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
});

// Helper function to determine color based on progress
const getProgressColor = (percentage: number): string => {
  if (percentage < 25) return '#ef4444';
  if (percentage < 50) return '#f59e0b';
  if (percentage < 75) return '#3b82f6';
  return '#10b981';
};

function getProgressLabel(percentage: number): string {
  if (percentage < 25) return 'Needs Attention';
  if (percentage < 50) return 'In Progress';
  if (percentage < 75) return 'Advancing';
  return 'On Track';
}

// Main UnifiedProjectView component
const UnifiedProjectView: React.FC<UnifiedProjectViewProps> = ({ 
  project, 
  onSave,
  useCubaDashboard = false
}) => {
  // Use performance monitoring
  useDetectSlowRender('UnifiedProjectView');
  
  const theme = useTheme();
  // State
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  
  // Initialize when project changes
  useEffect(() => {
    // Generate initial suggestions
    const initialSuggestions = aiService.performGapAnalysis(project);
    setSuggestions(initialSuggestions);
  }, [project]);
  
  // Calculate progress stats
  const { sections } = project;
  
  // Get counts for required items
  const requiredItemsCount = React.useMemo(() => {
    const counts = {
      total: 0,
      completed: 0,
      inProgress: 0,
      notStarted: 0,
    };
    
    sections.forEach(section => {
      section.subsections.forEach(subsection => {
        subsection.items.forEach(item => {
          if (item.isRequired) {
            counts.total += 1;
            
            if (item.status === 'COMPLETED') {
              counts.completed += 1;
            } else if (item.status === 'IN_PROGRESS') {
              counts.inProgress += 1;
            } else if (item.status === 'NOT_STARTED') {
              counts.notStarted += 1;
            }
          }
        });
      });
    });
    
    return counts;
  }, [sections]);
  
  // Top sections by weight
  const topSections = React.useMemo(() => 
    [...sections]
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 3),
  [sections]);
  
  // Handle export with useCallback
  const handleExportProject = useCallback(() => {
    exportProject(project);
  }, [project]);

  return (
    <Box sx={{ 
      p: 0,
      overflowY: 'auto',  // Ensure content is scrollable
      overflowX: 'hidden',
      height: '100%',
      pb: 4 // Add padding at bottom for better user experience
    }}>
      {/* Dashboard Section */}
      <Box 
        sx={{ 
          p: 2, 
          pb: 0,
          backgroundColor: useCubaDashboard 
            ? alpha(theme.palette.primary.main, 0.05)
            : 'transparent',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Greeting Section */}
        {useCubaDashboard && (
          <Card 
            sx={{ 
              p: 2, 
              mb: 3,
              borderRadius: 2,
              boxShadow: theme.shadows[2],
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.primary.light, 0.8)})`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {project.name || "Requirements Tracker"}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                    Track your project requirements and deliverables
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="secondary"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportProject}
                    sx={{ 
                      mt: 1, 
                      fontWeight: 'bold',
                      boxShadow: theme.shadows[2],
                      '&:hover': {
                        boxShadow: theme.shadows[4],
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Export Project
                  </Button>
                </Box>
                <Box 
                  sx={{ 
                    display: { xs: 'none', md: 'block' },
                    position: 'relative',
                    width: 160,
                    height: 160
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 160,
                      height: 160,
                      backgroundColor: alpha(theme.palette.background.paper, 0.2),
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="h4" component="div" fontWeight="bold">
                      {new Date().getHours().toString().padStart(2, '0')}:{new Date().getMinutes().toString().padStart(2, '0')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                right: -10,
                bottom: -20,
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.common.white, 0.05),
                zIndex: 0
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                right: 40,
                bottom: -50,
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.common.white, 0.1),
                zIndex: 0
              }}
            />
          </Card>
        )}

        {/* Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Overall Progress */}
          <Grid item xs={12} md={3}>
            <MetricCard
              title="Overall Progress"
              value={`${Math.round(project.overallProgress)}%`}
              icon={<DescriptionIcon />}
              color={theme.palette.primary.main}
              percentage={project.overallProgress}
            />
          </Grid>
          
          {/* Completed Items */}
          <Grid item xs={12} md={3}>
            <MetricCard
              title="Completed Items"
              value={requiredItemsCount.completed}
              total={requiredItemsCount.total}
              icon={<CheckCircleIcon />}
              color={theme.palette.success.main}
              percentage={requiredItemsCount.total > 0 ? (requiredItemsCount.completed / requiredItemsCount.total) * 100 : 0}
            />
          </Grid>
          
          {/* In Progress Items */}
          <Grid item xs={12} md={3}>
            <MetricCard
              title="In Progress Items"
              value={requiredItemsCount.inProgress}
              total={requiredItemsCount.total}
              icon={<HourglassEmptyIcon />}
              color={theme.palette.warning.main}
              percentage={requiredItemsCount.total > 0 ? (requiredItemsCount.inProgress / requiredItemsCount.total) * 100 : 0}
            />
          </Grid>
          
          {/* Not Started Items */}
          <Grid item xs={12} md={3}>
            <MetricCard
              title="Not Started Items"
              value={requiredItemsCount.notStarted}
              total={requiredItemsCount.total}
              icon={<ErrorOutlineIcon />}
              color={theme.palette.error.main}
              percentage={requiredItemsCount.total > 0 ? (requiredItemsCount.notStarted / requiredItemsCount.total) * 100 : 0}
            />
          </Grid>
        </Grid>
        
        {/* Key Sections Progress */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'visible'
          }}
        >
          <Box 
            sx={{ 
              px: 3, 
              py: 2, 
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box>
              <Typography variant="h6">Key Sections Progress</Typography>
              <Typography variant="body2" color="text.secondary">
                The most important sections based on weight
              </Typography>
            </Box>
            
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<FileDownloadIcon />}
              onClick={handleExportProject}
              size="small"
              sx={{ display: useCubaDashboard ? 'none' : 'flex' }}
            >
              Export Project
            </Button>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {topSections.map((section) => (
                <Grid item xs={12} md={4} key={section.id}>
                  <SectionProgressCard section={section} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>
      
      {/* Project Editor Section */}
      <Box 
        sx={{ 
          bgcolor: useCubaDashboard ? 'background.paper' : 'transparent',
          borderRadius: 2,
          mx: 2,
          mb: 3,
          boxShadow: useCubaDashboard ? theme.shadows[1] : 'none',
          overflow: 'hidden' // Ensure inner content doesn't overflow
        }}
      >
        <ProjectEditor project={project} onSave={onSave} />
      </Box>
    </Box>
  );
};

// Apply performance optimizations
export default withPerformanceOptimization(UnifiedProjectView, 'UnifiedProjectView');
